import { useEffect, useState } from 'react';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import { SmsPreferences } from './types';

const ENDPOINT = '/services_api/storefront/v1/sms_preferences';

function isUnauthorized(response: Response): boolean {
  return response.status === 401 || response.status === 403;
}

/**
 * Reads the customer's SMS preferences once the page has mounted.
 *
 * The same arrangement the auto renewals page uses: the props are per-site and
 * cached, so anything belonging to one customer has to arrive separately.
 */
export function useSmsPreferences(loginUrl: string) {
  const { buildUrl } = useUrlGenerator();
  const [preferences, setPreferences] = useState<SmsPreferences | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      try {
        const response = await fetch(buildUrl(`${ENDPOINT}.json`), { signal: controller.signal });

        if (isUnauthorized(response)) {
          window.location.assign(buildUrl(loginUrl));

          // Left loading on purpose: the page stays under its spinner while the
          // browser leaves, rather than flashing an empty form.
          return;
        }

        if (!response.ok) {
          throw new Error(`SMS preferences answered ${response.status}`);
        }

        setPreferences((await response.json()) as SmsPreferences);
        setIsLoading(false);
      } catch (caught: unknown) {
        if ((caught as Error)?.name === 'AbortError') {
          return;
        }

        console.error('Failed to load SMS preferences', caught);
        setHasFailed(true);
        setIsLoading(false);
      }
    })();

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { preferences, setPreferences, isLoading, hasFailed };
}

type SaveArgs = {
  loginUrl: string;
  onSuccess: (preferences: SmsPreferences) => void;
};

/**
 * Writes them back. Every save answers with the state that actually stuck, so a
 * list that refused is visible immediately rather than at the next page load.
 */
export function useSaveSmsPreferences({ loginUrl, onSuccess }: SaveArgs) {
  const { buildUrl } = useUrlGenerator();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [saved, setSaved] = useState(false);

  const post = async (path: string, body?: Record<string, unknown>) => {
    setIsSaving(true);
    setError(undefined);
    setSaved(false);

    try {
      const response = await fetch(buildUrl(`${ENDPOINT}${path}.json`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body ?? {}),
      });

      if (isUnauthorized(response)) {
        window.location.assign(buildUrl(loginUrl));

        return;
      }

      const payload = await response.json();

      if (!response.ok) {
        setError(payload?.error ?? 'Oops, something went wrong. Try again later.');
        setIsSaving(false);

        return;
      }

      onSuccess(payload as SmsPreferences);
      setSaved(true);
      setIsSaving(false);
    } catch (caught: unknown) {
      console.error('Failed to save SMS preferences', caught);
      setError('Oops, something went wrong. Try again later.');
      setIsSaving(false);
    }
  };

  return {
    save: (preferences: { telephone: string; marketing?: boolean; transactional?: boolean }) =>
      post('', preferences),
    unsubscribeAll: () => post('/unsubscribe_all'),
    isSaving,
    error,
    saved,
  };
}
